import "react-responsive-modal/styles.css";
import { useCallback } from "react";
import Modal from "react-responsive-modal";
import EnterEmailComponent from "./modals/Components/EnterEmailComponent";
import EnterPasswordComponent from "./modals/Components/EnterPasswordComponent";
import RegisterComponent from "./modals/Components/RegisterComponent";
import SignUpOnboardingComponent from "./modals/Components/SignUpOnboardingComponent";
import VerifyEmailComponent from "./modals/Components/VerifyEmailComponent";
import ForgotPasswordComponent from "./modals/Components/ForgotPasswordComponent";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import ResetForgotPasswordComponent from "./modals/Components/ResetForgotPasswordComponent";
import { useI18N } from "common/hooks";
import VerifyTermsAndConditions from "./modals/Components/VerifyTermsAndConditions";
import {
  showAcceptTAndCForNonLogin,
  termsAndConditionsAgreedKey,
} from "common/config";
import { trackEvent } from "common/analytics";
import { useAtomValue } from "jotai";
import { resetPasswordReasonAtom } from "@/store/atoms";

// Maps the active auth screen to the `auth_screen` value on the generated
// `auth_form_closed` event (vocabulary: auth_entry, credentials,
// email_verification, login, forgot_password — plus descriptive fallbacks).
const AUTH_SCREEN_BY_INDEX: Record<AuthModalScreen, string> = {
  [AuthModalScreen.EnterEmail]: "auth_entry",
  [AuthModalScreen.EnterPassword]: "login",
  [AuthModalScreen.Register]: "credentials",
  [AuthModalScreen.SignUpOnboarding]: "onboarding",
  [AuthModalScreen.VerifyEmail]: "email_verification",
  [AuthModalScreen.ForgotPassword]: "forgot_password",
  [AuthModalScreen.ResetForgotPassword]: "forgot_password",
  [AuthModalScreen.OnboardingScreen]: "onboarding",
  [AuthModalScreen.VerifyTermsAndConditions]: "terms_and_conditions",
};

const AuthModals = () => {
  const { isAuthModalVisible, hideModal, screenIndex, setScreenIndex, isBusy } =
    useAuthModals();
  const resetPasswordReason = useAtomValue(resetPasswordReasonAtom);
  const i18n = useI18N();

  // Check for Terms & Service acceptance
  const isTermsAccepted = useCallback(() => {
    const storedData = JSON.parse(
      localStorage.getItem(termsAndConditionsAgreedKey),
    );
    if (storedData) {
      const currentTime = new Date().getTime();
      const ninetyDaysInMs = 90 * 24 * 60 * 60 * 1000;
      return currentTime - storedData.timestamp < ninetyDaysInMs;
    }
    return false;
  }, []);

  return (
    <Modal
      focusTrapped
      animationDuration={0}
      blockScroll={false}
      open={isAuthModalVisible}
      closeOnEsc={!isBusy}
      closeOnOverlayClick={!isBusy}
      onClose={() => {
        // Container dismiss (backdrop tap / Escape) — generated tracking plan:
        // auth_form_closed, tagged with the screen that was active.
        trackEvent("auth_form_closed", {
          auth_screen: AUTH_SCREEN_BY_INDEX[screenIndex],
        });
        hideModal();
      }}
      classNames={{
        root: "auth-modal-root",
        modal: "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
        closeButton: "hidden",
      }}
      ariaLabelledby="modal-title"
      aria-hidden={!isAuthModalVisible}
    >
      {screenIndex === AuthModalScreen.EnterEmail && (
        <EnterEmailComponent
          onClose={() => {
            console.log(
              "showAcceptTAndCForNonLogin",
              showAcceptTAndCForNonLogin,
              isTermsAccepted(),
            );
            if (showAcceptTAndCForNonLogin == "Yes" && !isTermsAccepted()) {
              setScreenIndex(AuthModalScreen.VerifyTermsAndConditions);
            } else {
              hideModal();
            }
          }}
          title={i18n.get("loginOrCreateAccount")}
        />
      )}

      {screenIndex === AuthModalScreen.EnterPassword && (
        <EnterPasswordComponent
          onClose={() => {
            hideModal();
          }}
          onBack={() => {
            setScreenIndex(AuthModalScreen.EnterEmail);
          }}
          title={i18n.get("login")}
        />
      )}

      {screenIndex === AuthModalScreen.Register && (
        <RegisterComponent
          onClose={() => {
            setScreenIndex(AuthModalScreen.EnterEmail);
          }}
          title={i18n.get("createAccount")}
        />
      )}

      {screenIndex === AuthModalScreen.SignUpOnboarding && (
        <SignUpOnboardingComponent onClose={() => hideModal()} />
      )}

      {screenIndex === AuthModalScreen.VerifyEmail && <VerifyEmailComponent />}

      {screenIndex === AuthModalScreen.ForgotPassword && (
        <ForgotPasswordComponent
          onClose={() => hideModal()}
          title={i18n.get("forgotYourPassword")}
        />
      )}

      {screenIndex === AuthModalScreen.ResetForgotPassword && (
        <ResetForgotPasswordComponent
          onClose={() => hideModal()}
          title={i18n.get(
            resetPasswordReason === "policy"
              ? "passwordPolicyUpdateRequiredTitle"
              : "resetPassword",
          )}
        />
      )}

      {screenIndex === AuthModalScreen.VerifyTermsAndConditions && (
        <VerifyTermsAndConditions
          onClose={() => hideModal()}
          title={i18n.get("termsOfUse")}
        />
      )}
    </Modal>
  );
};

export default AuthModals;
